import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCurrency } from '../context/CurrencyContext'
import { MapPin, TrendingUp, Clock } from 'lucide-react'

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  tokenized: 'bg-blue-100 text-blue-700 border-blue-200',
  sold_out: 'bg-red-100 text-red-700 border-red-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
}

export function PropertyCard({ property, index = 0 }) {
  const { t } = useTranslation()
  const { formatPrice } = useCurrency()
  const {
    id, name, property_type, city, country, total_value, token_price,
    total_tokens, tokens_sold = 0, min_investment, expected_yield,
    investment_term, status, images = [],
  } = property

  const progress = total_tokens > 0 ? (tokens_sold / total_tokens) * 100 : 0
  const heroImage = images[0] || 'https://images.unsplash.com/photo-1760611655987-d348d6d28174?w=600&q=80'

  const propertyTypeLabels = {
    residential: t('marketplace.residential'),
    commercial: t('marketplace.commercial'),
    industrial: t('marketplace.industrial'),
    mixed_use: t('marketplace.mixedUse'),
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} whileHover={{ y: -4 }} className="group">
      <Link to={`/property/${id}`} data-testid={`property-card-${id}`}>
        <div className="relative overflow-hidden bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300">
          <div className="relative h-52 overflow-hidden">
            <img src={heroImage} alt={name} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-secondary/60 via-transparent to-transparent" />
            <div className="absolute top-4 left-4">
              <span className={`px-2 py-1 text-xs font-semibold uppercase tracking-widest border ${statusColors[status] || statusColors.draft}`}>
                {status?.replace('_', ' ')}
              </span>
            </div>
            <div className="absolute bottom-4 left-4">
              <span className="px-2 py-1 bg-white/90 text-brand-secondary text-xs font-semibold uppercase tracking-wider">
                {propertyTypeLabels[property_type] || property_type}
              </span>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <h3 className="text-lg font-heading text-brand-secondary group-hover:text-brand-primary transition-colors line-clamp-1">{name}</h3>
              <div className="flex items-center gap-1 mt-1 text-slate-500">
                <MapPin className="w-3 h-3" /><span className="text-sm">{city}, {country}</span>
              </div>
            </div>

            <div className="w-12 h-px bg-brand-primary" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">{t('property.totalValue')}</span>
                <p className="text-base font-semibold text-brand-secondary font-mono" data-testid={`card-value-${id}`}>{formatPrice(total_value)}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">{t('property.tokenPrice')}</span>
                <p className="text-base font-semibold text-brand-secondary font-mono" data-testid={`card-token-price-${id}`}>{formatPrice(token_price, { decimals: 2 })}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">{t('property.expectedYield')}</span>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-emerald-600" />
                  <p className="text-base font-semibold text-emerald-600 font-mono">{expected_yield}%</p>
                </div>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">{t('property.term')}</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-slate-500" />
                  <p className="text-base font-semibold text-brand-secondary">{investment_term} mo</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 uppercase tracking-wider">{t('property.fundingProgress')}</span>
                <span className="text-brand-secondary font-semibold font-mono">{progress.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-slate-100 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ delay: 0.5, duration: 0.8 }} className="h-full bg-brand-primary" />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{tokens_sold.toLocaleString()} / {total_tokens.toLocaleString()} tokens</span>
                <span className="text-brand-primary font-semibold">Min {formatPrice(min_investment)}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}
